// To use confetti, run: npm install canvas-confetti
import React, { useState, useEffect, useRef } from 'react';
// @ts-ignore
import confetti from 'canvas-confetti';

const ETF_LINKS: Record<string, { name: string; url: string }> = {
  'S&P500': {
    name: 'S&P 500',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00B3YCGJ38',
  },
  'S&P Screened': {
    name: 'S&P Screened',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BFXR5Q31',
  },
  'SUSW': {
    name: 'SUSW',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BYX2JD69',
  },
  'V3AA': {
    name: 'V3AA',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BNG8L278',
  },
  'UETW': {
    name: 'UETW',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BD4TXV59',
  },
  'VWCE': {
    name: 'VWCE',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BK5BQT80',
  },
  'V80A': {
    name: 'V80A',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BMVB5R75',
  },
  'V60A': {
    name: 'V60A',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BMVB5P51',
  },
  'F702': {
    name: 'F702',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=DE000ETF7029',
  },
  'JPGL': {
    name: 'JPGL',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE00BJRCLL96',
  },
  'LVLE': {
    name: 'LVLE',
    url: 'https://www.justetf.com/en/etf-profile.html?isin=IE000XIBT2R7',
  },
};

// Add detailed info for each ETF
const ETF_DETAILS: Record<string, { index: string; performance1y?: string; cagr3y?: string; cagr5y?: string; aum: string; max?: string }> = {
  'S&P500': {
    index: 'tracks S&P 500\u00ae index',
    performance1y: '+14.16%',
    cagr3y: '+20.74%',
    cagr5y: '+24.25%',
    aum: '31237',
    max: '+769%',
  },
  'S&P Screened': {
    index: 'tracks Solactive US Large & Mid Cap Screened index',
    performance1y: '+14.97%',
    cagr3y: '+21.48%',
    cagr5y: '+23.72%',
    aum: '1076',
    max: '+172.27%',
  },
  'SUSW': {
    index: 'tracks MSCI World\u00ae SRI Select Reduced Fossil Fuels index',
    performance1y: '+9.31%',
    cagr3y: '+13.23%',
    cagr5y: '+16.72%',
    aum: '6589',
    max: '+142.26%',
  },
  'V3AA': {
    index: 'tracks FTSE Global All Cap Choice index',
    performance1y: '+14.78%',
    cagr3y: '+18.09%',
    // 59.14/(3+20/12)
    cagr5y: '+12.67%',
    aum: '686',
    max: '+59.14%',
  },
  'UETW': {
    index: 'tracks MSCI World\u00ae index',
    performance1y: '+15.32%',
    cagr3y: '+18.47%',
    cagr5y: '+21.85%',
    aum: '4171',
    max: '+122%',
  },
  'VWCE': {
    index: 'tracks FTSE All-World index',
    performance1y: '+15.55%',
    cagr3y: '+18.76%',
    cagr5y: '+18.93%',
    aum: '25170',
    max: '+103.45%',
  },
  'V80A': {
    index: 'tracks Vanguard LifeStrategy\u00ae 80% Equity',
    performance1y: '+13.02%',
    cagr3y: '+15.47%',
    aum: '839',
    max: '+58.66%',
  },
  'V60A': {
    index: 'tracks Vanguard LifeStrategy\u00ae 60% Equity',
    performance1y: '+10.77%',
    cagr3y: '+12.26%',
    aum: '619',
    max: '+37.48%',
  },
  'JPGL': {
    index: 'tracks JPMorgan\u00ae Diversified Factor Global Developed (Low Volatility) Equity',
    performance1y: '+4.53%',
    cagr3y: '+9.28%',
    cagr5y: '+15.38%',
    aum: '182',
    max: '+71.06%',
  },
  'F702': {
    index: 'tracks Amundi Multi-Asset Portfolio Defensive Equity',
    performance1y: '+11.33%',
    cagr3y: '+11.84%',
    aum: '51',
    max: '+48.36%',
  },
  'LVLE': {
    index: 'tracks Invesco Quantitative Strategies\u00ae Global Equity Low Volatility',
    performance1y: '+13.23%',
    cagr3y: '+17%',
    aum: '57',
    max: '+44.31%',
  },
};

const questions = [
  {
    key: 'risk',
    question: 'Your risk profile:',
    options: [
      { value: 'maximum', label: 'Higher risk' },
      { value: 'balanced', label: 'Balanced' },
      { value: 'defensive', label: 'Defensive' },
    ],
  },
  {
    key: 'active',
    question: 'Do you prefer an actively managed solution (frequently restrategized or reorganized) or a passive management style (will only track the predefined index)',
    options: [
      { value: 'no', label: 'Passive ETF' },
      { value: 'yes', label: 'Active ETF' }
    ],
    showFor: (answers: any) => answers.risk === 'defensive',
  },
  {
    key: 'portfolio',
    question: 'Do you prefer an actively managed portfolio, comprised of the following?',
    options: [
      { value: '100_stocks', label: '100% stocks (EUR Currency Hedged)' },
      { value: '80_20', label: '80% stocks and 20% bonds' },
      { value: '60_40', label: '60% stocks and 40% bonds' },
      { value: '40_50_10', label: '40% stocks, 50% bonds, 10% commodities' },
    ],
  },
  {
    key: 'esg',
    question: 'Do you have a preference for sustainable, environmental, ESG-positive solutions? (This will eliminate for example tobacco, chemical weapon companies, …)',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'region',
    question: 'Do you have a preference for Developed World (excludes emerging countries such as China, India, Africa etc.) or rather All-World (which encompasses 90%+ of World leading stocks)?',
    options: [
      { value: 'developed', label: 'Developed World' },
      { value: 'all', label: 'All-World' },
    ],
  },
  {
    key: 'vwca',
    question: 'Do you prefer an actively managed solution, comprised of the following?',
    options: [
      { value: '100', label: '100% stocks (All-World)' },
      { value: '80', label: '80% stocks and 20% bonds (All-World)' },
      { value: '60', label: '60% stocks and 40% bonds (All-World)' },
    ],
  },
];

function getResult(answers: any): string | null {
  const { risk, esg, region, vwca, active, portfolio } = answers;
  
  if (risk === 'maximum') {
    if (esg === 'no') return 'S&P500';
    if (esg === 'yes') return 'S&P Screened';
  }
  
  if (risk === 'balanced') {
    if (esg === 'yes') {
      if (region === 'developed') return 'SUSW';
      if (region === 'all') return 'V3AA';
    }
    if (esg === 'no') {
      if (region === 'developed') return 'UETW';
      if (region === 'all') {
        if (vwca === '100') return 'VWCE';
        if (vwca === '80') return 'V80A';
        if (vwca === '60') return 'V60A';
      }
    }
  }
  
  if (risk === 'defensive') {
    if (active === 'no') {
      return 'JPGL';
    }
    if (active === 'yes') {
      switch (portfolio) {
        case '100_stocks':
          return 'LVLE';
        case '80_20':
          return 'V80A';
        case '60_40':
          return 'V60A';
        case '40_50_10':
          return 'F702';
        default:
          return 'JPGL';
      }
    }
  }
  
  return null;
}

const Tooltip: React.FC<React.PropsWithChildren<{ text: string }>> = ({ text, children }) => {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onTouchStart={() => setShow(v => !v)}
    >
      {children}
      {show && (
        <span style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: '120%',
          background: '#222',
          color: '#fff',
          padding: '0.4em 0.8em',
          borderRadius: 6,
          fontSize: '0.98em',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
        }}>{text}</span>
      )}
    </span>
  );
};

const PerformanceBarGraph: React.FC<{ details: { performance1y?: string; cagr3y?: string; cagr5y?: string } }> = ({ details }) => {
  // Parse numbers from strings like '+4.75%'
  const parse = (val?: string) => val ? parseFloat(val.replace(/[^\d.-]/g, '')) : undefined;
  const y1 = details.performance1y ? parse(details.performance1y) : undefined;
  const y3 = details.cagr3y ? parse(details.cagr3y) : undefined;
  const y5 = details.cagr5y ? parse(details.cagr5y) : undefined;
  // Interpolate 2y and 4y if possible
  const y2 = (y1 !== undefined && y3 !== undefined) ? (y1 + y3) / 2 : undefined;
  const y4 = (y3 !== undefined && y5 !== undefined) ? (y3 + y5) / 2 : undefined;
  // Build data array: 1y, 2y (if present), 3y, 4y (if present), 5y
  const data = [
    y1 !== undefined ? { label: '1y', value: y1, showLabel: true, showValue: true } : null,
    y2 !== undefined ? { label: '', value: y2, showLabel: false, showValue: false } : null,
    y3 !== undefined ? { label: '3y', value: y3, showLabel: true, showValue: true } : null,
    y4 !== undefined ? { label: '', value: y4, showLabel: false, showValue: false } : null,
    y5 !== undefined ? { label: '5y', value: y5, showLabel: true, showValue: true } : null,
  ].filter(Boolean) as { label: string; value: number; showLabel: boolean; showValue: boolean }[];
  if (data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const barWidth = 28;
  const barGap = 16;
  const height = 100;
  const width = data.length * (barWidth + barGap) - barGap + 80; // extra space for 'annualized'
  return (
    <div style={{ marginTop: '0.2rem', marginBottom: '0.8rem', textAlign: 'center' }}>
      <svg width={width} height={height} style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}>
        {data.map((d, i) => {
          const barHeight = Math.max(2, (d.value / max) * (height - 44));
          const x = 16 + i * (barWidth + barGap);
          return (
            <g key={i}>
              <rect
                x={x}
                y={height - barHeight - 36}
                width={barWidth}
                height={barHeight}
                fill="#fbbf24"
                rx={6}
              />
              {/* Only show value above for 1y, 3y, 5y bars */}
              {d.showValue && (
                <text
                  x={x + barWidth / 2}
                  y={height - barHeight - 44}
                  textAnchor="middle"
                  fontSize="1em"
                  fill="#fff"
                  fontWeight="bold"
                >{d.value}%</text>
              )}
              {/* Only show x-axis label for 1y, 3y, 5y */}
              {d.showLabel && (
                <text
                  x={x + barWidth / 2}
                  y={height - 10}
                  textAnchor="middle"
                  fontSize="1.05em"
                  fill="#fff"
                  fontWeight="bold"
                >{d.label}</text>
              )}
            </g>
          );
        })}
      </svg>
      {/* 'annualized' label below the graph, right-aligned */}
      <div style={{ marginTop: '0.3rem', fontSize: '0.98em', color: '#fff', opacity: 0.7, fontWeight: 400, letterSpacing: '0.01em', textAlign: 'right', width: '100%' }}>annualized</div>
    </div>
  );
};

const DecisionTree: React.FC = () => {
  const [answers, setAnswers] = useState<any>({});
  const [step, setStep] = useState(0);
  const [result, setResult] = useState<string | null>(null);
  const [showSplash, setShowSplash] = useState(true);
  const confettiFired = useRef(false);
  const questionSectionRef = useRef<HTMLDivElement>(null);
  const resultSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSplash && !result && questionSectionRef.current) {
      questionSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showSplash, step, result]);

  useEffect(() => {
    if (result && !confettiFired.current && resultSectionRef.current) {
      confettiFired.current = true;
      resultSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#2563eb', '#1a365d', '#fbbf24', '#22d3ee', '#f87171'],
        scalar: 1.1,
      });
      setTimeout(() => { confettiFired.current = false; }, 2000);
    }
  }, [result]);

  // Inject Ko-fi widget only on result page
  useEffect(() => {
    if (result) {
      // Only add script if not already present
      if (!document.getElementById('kofi_widget_overlay')) {
        const script = document.createElement('script');
        script.id = 'kofi_widget_overlay';
        script.src = 'https://storage.ko-fi.com/cdn/scripts/overlay-widget.js';
        script.async = true;
        script.onload = () => {
          // @ts-ignore
          const kofiWidgetOverlay = (window as any).kofiWidgetOverlay;
          if (kofiWidgetOverlay) {
            kofiWidgetOverlay.draw('arthurjs', {
              'type': 'floating-chat',
              'floating-chat.donateButton.text': 'Tip with Ko-Fi',
              'floating-chat.donateButton.background-color': '#2563eb',
              'floating-chat.donateButton.text-color': '#fff'
            });
          }
        };
        document.body.appendChild(script);
      } else {
        // @ts-ignore
        const kofiWidgetOverlay = (window as any).kofiWidgetOverlay;
        if (kofiWidgetOverlay) {
          kofiWidgetOverlay.draw('arthurjs', {
            'type': 'floating-chat',
            'floating-chat.donateButton.text': 'Tip with Ko-Fi',
            'floating-chat.donateButton.background-color': '#2563eb',
            'floating-chat.donateButton.text-color': '#fff'
          });
        }
      }
    }
    // Cleanup: only remove widget overlay, not the script
    return () => {
      const kofiOverlay = document.getElementById('kofi-widget-overlay');
      if (kofiOverlay) kofiOverlay.remove();
    };
  }, [result]);

  const handleOption = (value: string) => {
    const newAnswers = { ...answers };
    const currentKey = getCurrentQuestionKey();
    newAnswers[currentKey] = value;
    setAnswers(newAnswers);

    // Determine next step or result
    if (currentKey === 'risk') {
      if (value === 'defensive') {
        setStep(1); // Go to active question
      } else if (value === 'maximum' || value === 'balanced') {
        setStep(6); // Go to ESG question for maximum/balanced path
      }
    } else if (currentKey === 'active' && answers.risk === 'defensive') {
      if (value === 'no') {
        const res = getResult({ ...newAnswers });
        setResult(res);
        setStep(questions.length);
      } else {
        setStep(2); // Go to portfolio question
      }
    } else if (currentKey === 'portfolio' && answers.risk === 'defensive') {
      const res = getResult({ ...newAnswers });
      setResult(res);
      setStep(questions.length);
    } else if (currentKey === 'esg') {
      if (answers.risk === 'maximum') {
        const res = getResult({ ...newAnswers });
        setResult(res);
        setStep(questions.length);
      } else if (answers.risk === 'balanced') {
        setStep(7); // Go to region question
      }
    } else if (currentKey === 'region' && answers.risk === 'balanced') {
      if (answers.esg === 'yes' || value === 'developed') {
        const res = getResult({ ...newAnswers });
        setResult(res);
        setStep(questions.length);
      } else if (value === 'all') {
        setStep(8); // Go to vwca question
      }
    } else if (currentKey === 'vwca') {
      const res = getResult({ ...newAnswers });
      setResult(res);
      setStep(questions.length);
    }
  };

  const getCurrentQuestionKey = () => {
    if (step === 0) return 'risk';
    if (answers.risk === 'defensive') {
      if (step === 1) return 'active';
      if (step === 2) return 'portfolio';
      return ''; // Defensive path should not see other questions
    }
    if (step === 6) return 'esg';
    if (step === 7) return 'region';
    if (step === 8) return 'vwca';
    return '';
  };

  const getCurrentQuestion = () => {
    const key = getCurrentQuestionKey();
    return questions.find(q => q.key === key);
  };

  const handleRestart = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
    setShowSplash(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    if (step === 0) return;
    
    const currentKey = getCurrentQuestionKey();
    const newAnswers = { ...answers };
    delete newAnswers[currentKey];
    
    // Determine the previous step based on current path
    let previousStep = step - 1;
    
    if (answers.risk === 'defensive') {
      if (step === 2) {
        // From portfolio, go back to active
        previousStep = 1;
      } else if (step === 1) {
        // From active, go back to risk
        previousStep = 0;
      }
    } else if (answers.risk === 'balanced') {
      if (step === 8) {
        // From vwca, go back to region
        previousStep = 7;
      } else if (step === 7) {
        // From region, go back to esg
        previousStep = 6;
      } else if (step === 6) {
        // From esg, go back to risk
        previousStep = 0;
      }
    } else if (answers.risk === 'maximum') {
      if (step === 6) {
        // From esg, go back to risk
        previousStep = 0;
      }
    }
    
    setStep(previousStep);
    setResult(null);
    setAnswers(newAnswers);
  };

  // Progress indicator
  const getProgress = () => {
    if (result) return null;
    
    if (step === 0) {
      return 'Step 1 of 1'; // Initial risk question
    }
    
    if (answers.risk === 'defensive') {
      const totalSteps = answers.active === 'no' ? 2 : 3;
      return `Step ${step + 1} of ${totalSteps}`;
    }
    
    if (answers.risk === 'maximum') {
      const currentStep = step === 6 ? 2 : 1;
      return `Step ${currentStep} of 2`; // Risk + ESG = 2 steps
    }
    
    if (answers.risk === 'balanced') {
      let totalSteps = 3; // Risk + ESG + Region
      if (answers.esg === 'no' && answers.region === 'all') {
        totalSteps = 4; // Add VWCA step
      }
      let currentStep;
      if (step === 6) currentStep = 2; // ESG
      else if (step === 7) currentStep = 3; // Region
      else if (step === 8) currentStep = 4; // VWCA
      else currentStep = 1; // Risk
      return `Step ${currentStep} of ${totalSteps}`;
    }
    
    return `Step 1 of 1`; // Fallback
  };

  if (showSplash) {
    return (
      <section className="hero-section">
        <img src="/which-etf-logo.png" alt="Which ETF Logo" className="hero-logo" />
        <h1 className="hero-title">Welcome to Which ETF</h1>
        <p className="hero-desc">
          This wizard will help you find which ETF to buy that matches your investment profile.
        </p>
        <button
          className="hero-btn"
          onClick={() => setShowSplash(false)}
        >
          Start
        </button>
      </section>
    );
  }

  return (
    <>
      {!result && (
        <section className="question-section" ref={questionSectionRef}>
          <div className="question-block">
            <div className="progress-indicator">{getProgress()}</div>
            <div className="question-title">{getCurrentQuestion()?.question}</div>
            <div className="question-btn-group">
              {getCurrentQuestion()?.options.map(opt => (
                <button
                  key={opt.value}
                  className="question-btn"
                  onClick={() => handleOption(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                className="back-btn"
                onClick={handleBack}
              >
                Back
              </button>
            )}
          </div>
        </section>
      )}
      {result && (
        <section className="result-section" ref={resultSectionRef}>
          <div className="result-card">
            <div className="result-title" style={{ marginBottom: '1.5rem' }}>Your result:</div>
            <div className="result-value" style={{ margin: '0 0 2.2rem 0' }}>
              {ETF_LINKS[result] ? (
                <a
                  href={ETF_LINKS[result].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 700 }}
                >
                  {ETF_LINKS[result].name}
                </a>
              ) : (
                result
              )}
            </div>
            {/* Show ETF details if available */}
            {ETF_DETAILS[result] && (
              <div className="result-details" style={{ marginTop: '0', marginBottom: '2.2rem', color: '#fff', fontSize: '1.08rem', textAlign: 'left', background: 'rgba(30,41,59,0.18)', borderRadius: 12, padding: '1.1rem 1.5rem', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto', boxShadow: '0 2px 12px rgba(37,99,235,0.10)' }}>
                <div><strong>Index:</strong> {ETF_DETAILS[result].index}</div>
                <div><strong>Total assets <Tooltip text="Net assets, recorded in millions">(MM)</Tooltip>:</strong> {ETF_DETAILS[result].aum} m</div>
                <br />
                {ETF_DETAILS[result].performance1y && (
                  <div><strong>Performance (1 year):</strong> {ETF_DETAILS[result].performance1y}</div>
                )}
                {ETF_DETAILS[result].cagr3y && (
                  <div><strong><Tooltip text="Compound annual growth rate"><span className="cagr-underline">CAGR</span></Tooltip> (3 year):</strong> {ETF_DETAILS[result].cagr3y}</div>
                )}
                {ETF_DETAILS[result].cagr5y && (
                  <div><strong><Tooltip text="Compound annual growth rate"><span className="cagr-underline">CAGR</span></Tooltip> (5 year):</strong> {ETF_DETAILS[result].cagr5y}</div>
                )}
                {/* Since inception line */}
                <div><strong>Since inception:</strong> {ETF_DETAILS[result].max || '+80%'}</div>
                <div style={{ height: '2.2rem' }} />
                <PerformanceBarGraph details={ETF_DETAILS[result]} />
                {/* More vertical spacing after the graph */}
                <div style={{ height: '1.2rem' }} />
              </div>
            )}
            <button className="result-btn" style={{ marginTop: '0.8rem' }} onClick={handleRestart}>Restart</button>
          </div>
        </section>
      )}
    </>
  );
};

export default DecisionTree; 